<div class="air-review">
  <header class="air-review__summary">
    <p class="air-review__summary-counts" data-testid="review-summary">
      {{ summaryLabel() }}
    </p>
    <div class="air-review__summary-right">
      <span class="air-review__total" data-testid="review-total">{{
        countLabel()
      }}</span>
      <p-button
        icon="pi pi-refresh"
        severity="secondary"
        [text]="true"
        [rounded]="true"
        size="small"
        [loading]="loading()"
        ariaLabel="Refresh activities"
        pTooltip="Refresh"
        tooltipPosition="left"
        (onClick)="refresh()"
      />
    </div>
  </header>

  <div class="air-filters" role="group" aria-label="Filter by status">
    @for (status of primaryStatuses; track status) {
      <button
        type="button"
        class="air-chip"
        [class.air-chip--active]="isActive(status)"
        [attr.aria-pressed]="isActive(status)"
        [attr.data-testid]="'review-filter-' + status"
        (click)="toggleStatus(status)"
      >
        <span
          class="air-chip__dot"
          [ngClass]="statusMeta[status].dotClass"
        ></span>
        {{ statusMeta[status].label }}
        <span class="air-chip__count">{{ countFor(status) }}</span>
      </button>
    }

    @if (showAllFilters()) {
      @for (status of secondaryStatuses; track status) {
        <button
          type="button"
          class="air-chip"
          [class.air-chip--active]="isActive(status)"
          [attr.aria-pressed]="isActive(status)"
          [attr.data-testid]="'review-filter-' + status"
          (click)="toggleStatus(status)"
        >
          <span
            class="air-chip__dot"
            [ngClass]="statusMeta[status].dotClass"
          ></span>
          {{ statusMeta[status].label }}
          <span class="air-chip__count">{{ countFor(status) }}</span>
        </button>
      }
    }

    <button
      type="button"
      class="air-filters__more"
      data-testid="review-filter-more"
      (click)="showAllFilters.set(!showAllFilters())"
    >
      {{ showAllFilters() ? 'Fewer' : 'More' }}
      <i
        class="pi"
        [class.pi-angle-up]="showAllFilters()"
        [class.pi-angle-down]="!showAllFilters()"
      ></i>
    </button>

    @if (hasFilters) {
      <button
        type="button"
        class="air-filters__reset"
        data-testid="review-filter-reset"
        (click)="resetFilters()"
      >
        Reset
      </button>
    }
  </div>

  @if (countsCaveat(); as caveat) {
    <p class="air-review__caveat" data-testid="review-caveat">
      <i class="pi pi-info-circle"></i>
      {{ caveat }}
    </p>
  }

  @if (loading()) {
    <div class="air-list" data-testid="review-loading">
      @for (row of [1, 2, 3, 4]; track row) {
        <div class="air-row air-row--skeleton">
          <div class="flex flex-col gap-2 flex-1 min-w-0">
            <p-skeleton width="22rem" height="1rem" />
            <p-skeleton width="30rem" height="0.75rem" />
            <p-skeleton width="6rem" height="0.625rem" />
          </div>
          <p-skeleton width="8rem" height="1.25rem" />
        </div>
      }
    </div>
  } @else if (error(); as errorMessage) {
    <div class="air-review__error" data-testid="review-error">
      <p class="air-review__error-title">
        <i class="pi pi-exclamation-triangle"></i>
        Could not load activities
      </p>
      <p class="air-review__error-detail">{{ errorMessage }}</p>
      <p-button
        label="Try again"
        size="small"
        severity="secondary"
        [outlined]="true"
        (onClick)="refresh()"
      />
    </div>
  } @else if (visible().length === 0) {
    <div class="air-review__empty" data-testid="review-empty">
      <i class="pi pi-inbox"></i>
      @if (hasFilters) {
        <p>No activities match the selected filters.</p>
        <p-button
          label="Clear filters"
          size="small"
          [text]="true"
          (onClick)="resetFilters()"
        />
      } @else {
        <p>Nothing in the review queue.</p>
        <p class="air-review__empty-hint">
          Activities appear here once the conversation router agent proposes
          actions for a live conversation.
        </p>
      }
    </div>
  } @else {
    <ul class="air-list" data-testid="review-list">
      @for (activity of visible(); track activity.activity_id) {
        <li>
          <button
            type="button"
            class="air-row"
            [attr.data-status]="activity.status"
            [attr.data-testid]="'review-row-' + activity.activity_id"
            (click)="openActivity(activity)"
          >
            <div class="air-row__body">
              <div class="air-row__heading">
                @if (isNew(activity)) {
                  <span
                    class="air-row__unseen"
                    pTooltip="Not opened yet"
                    tooltipPosition="top"
                  ></span>
                }
                <h3 class="air-row__title">{{ activity.title }}</h3>
                <span class="air-row__action-count">{{
                  actionCountFor(activity)
                }}</span>
              </div>

              @if (quoteFor(activity); as quote) {
                <p class="air-row__quote">&ldquo;{{ quote }}&rdquo;</p>
              }

              <div class="air-row__meta">
                <span>{{ since(activity.created_at) }}</span>
                @if (waiting(activity)) {
                  <span class="air-row__waiting">&middot; waiting</span>
                }
                @if (hasNotes(activity)) {
                  <span class="air-row__badge air-row__badge--note">
                    <i class="pi pi-flag"></i>
                    Note on file
                  </span>
                }
              </div>
            </div>

            <div class="air-row__side">
              <span
                class="air-status"
                [ngClass]="statusMeta[activity.status].chipClass"
              >
                <span
                  class="air-status__dot"
                  [ngClass]="statusMeta[activity.status].dotClass"
                ></span>
                {{ statusMeta[activity.status].label }}
              </span>
              <span class="air-row__cta"
                >Review <i class="pi pi-arrow-right"></i
              ></span>
            </div>
          </button>
        </li>
      }
    </ul>

    <footer class="air-pager">
      <span class="air-pager__range" data-testid="review-range">{{
        rangeLabel()
      }}</span>
      @if (pageCount() > 1) {
        <div class="air-pager__controls">
          <button
            type="button"
            class="air-pager__step"
            [disabled]="page() === 0"
            (click)="goToPage(page() - 1)"
          >
            <i class="pi pi-angle-left"></i> Previous
          </button>
          @for (index of pageNumbers(); track index) {
            <button
              type="button"
              class="air-pager__page"
              [class.air-pager__page--active]="index === page()"
              [attr.aria-current]="index === page() ? 'page' : null"
              (click)="goToPage(index)"
            >
              {{ index + 1 }}
            </button>
          }
          <button
            type="button"
            class="air-pager__step"
            [disabled]="page() === pageCount() - 1"
            (click)="goToPage(page() + 1)"
          >
            Next <i class="pi pi-angle-right"></i>
          </button>
        </div>
      }
    </footer>
  }
</div>

@if (selectedActivity(); as activity) {
  <air-activity-detail-dialog
    [activity]="activity"
    [clientName]="clientName()"
    [visible]="detailVisible()"
    (visibleChange)="detailVisible.set($event)"
    (changed)="onActivityChanged()"
  />
}
